/**
 * E2E renderer probe — Playwright drives the `registration_completed` browser
 * writer through the real gate + latch + `track()` pipeline via
 * `globalThis.__irisRegistrationTelemetry`.
 *
 * WHY: the e2e account already exists, so its ID token never carries the
 * post-login Action's `first_login` claim and `maybeTrackRegistrationCompleted`
 * returns before it emits. Jest covers the gate decision. This probe hands the
 * SAME writer a claim-bearing user plus a caller-chosen user id, so the
 * send-boundary receipt (posthog-registration-completed.spec.ts) can prove the
 * canonical name leaves the app, and can prove the localStorage latch keeps a
 * repeat first-login silent — the two behaviours the product path relies on.
 * The spec picks a fresh user id per run so the latch never blocks a rerun.
 *
 * Same IRIS_E2E_TEST gate as onboarding-telemetry-probe.ts. No product
 * behaviour. Per rule: stay-in-scope (ai.knowledge/global).
 * Per knowledge: posthog-ci-receipt-selection (ai.knowledge/platform) — the
 * receipt this probe feeds is how posthog-selection picks the new event.
 */
import type { User as Auth0User } from '@auth0/auth0-react';
import { FIRST_LOGIN_CLAIM, maybeTrackRegistrationCompleted } from '#~/auth/auth-journey.js';

interface RegistrationTelemetryProbe {
  /** Run the first-login writer for `userId` exactly as the synchronizer does after identify. */
  completed(userId: string): void;
}

/**
 * Renderer-process gate. `process.env` is NOT populated in this renderer even
 * with nodeIntegration — the preload bridge is the only place the inherited OS
 * environment is visible (see calibration-probe.ts for the full note).
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/**
 * Drive the real writer with a claim-bearing user. Exported so the unit test can
 * pin that the probe exercises the product gate (claim + latch) rather than
 * calling the tracker around it — a probe that skipped the gate would receipt a
 * path the product never runs.
 */
export function driveRegistrationCompleted(userId: string): void {
  // Only the namespaced claim is read by the writer; no other Auth0 profile
  // field influences the decision, so this is the whole shape it needs.
  const firstLoginUser = { [FIRST_LOGIN_CLAIM]: true } as Auth0User;
  maybeTrackRegistrationCompleted(firstLoginUser, userId);
}

if (isE2eEnabled()) {
  const probe: RegistrationTelemetryProbe = {
    completed(userId: string): void {
      driveRegistrationCompleted(userId);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisRegistrationTelemetry = probe;
}
